import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Search,
  X,
  Plus,
  Download,
  ClipboardList,
  FileSpreadsheet,
  Eye,
  Edit,
  CheckCircle2,
  Clock,
  PlayCircle,
  Pause,
  Play,
  Zap,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Filter,
  Settings2,
} from "lucide-react";
import { toast } from "sonner";
import { Progress } from "@/components/ui/progress";
import {
  mockStockTakes,
  StockTake,
  StockTakeStatus,
  calculateProgress,
  getStatusColor,
  getTypeColor,
  formatRecurrence,
} from "@/data/mockStockTakes";
import { getAllSavedStockTakes, SavedStockTake, deleteSavedStockTake } from "@/lib/stock-take-storage";
import { mockWarehouses } from "@/data/mockWarehousesLocations";
import { CreateStockTakeDialog } from "@/components/stock-takes/create-stock-take-dialog";
import { CreateScheduleDialog } from "@/components/stock-takes/create-schedule-dialog";
import { StockTakeDetailsDrawer } from "@/components/stock-takes/stock-take-details-drawer";
import { exportToCSV, exportToExcel, getCurrentDateForFilename } from "@/lib/export-utils";
import { useNavigation } from "@/components/layout/app-layout";
import { HelpButton } from "@/components/help-button";
import { ContextualHelpDrawer } from "@/components/contextual-help-drawer";
import { useContextualHelp } from "@/hooks/use-contextual-help";
import { ConfigureColumnsModal, ColumnDefinition, ColumnConfig } from "@/components/configure-columns-modal";
import { AdvancedFiltersModal } from "@/components/advanced-filters-modal";
import { FilterRow, FilterLogic, ActiveFilterChip, FilterField } from "@/types/filters";
import { applyFilters, generateFilterChips } from "@/lib/stock-take-filter-utils";
import { StockTakeFilterInput, StockTakeSortInput } from "@/hooks/use-stock-takes";
import { useScreenMode } from "@/providers/ModeProvider";
import { useStockTakes } from "@/hooks/use-stock-takes";
import { GlobalModeToggle } from "@/components/global-mode-toggle";
import { StaticMetricCardAlt } from "@/components/StaticMetricCardAlt";
import { SortOrder } from "../hooks/use-graphql-fetch";

// All available columns with descriptions
const ALL_COLUMNS: ColumnDefinition[] = [
  { id: "id", name: "ID", description: "Stock take identifier" },
  { id: "description", name: "Description", description: "Stock take description" },
  { id: "tags", name: "Tags", description: "Associated tags" },
  { id: "type", name: "Type", description: "Stock take type (Full, Partial, Cycle Count, etc.)" },
  { id: "status", name: "Status", description: "Current status" },
  { id: "warehouse", name: "Warehouse", description: "Warehouse location" },
  { id: "recurrence", name: "Recurrence", description: "Recurrence schedule" },
  { id: "progress", name: "Progress", description: "Completion progress" },
  { id: "discrepancies", name: "Discrepancies", description: "Number of discrepancies found" },
  { id: "assignedTo", name: "Assigned To", description: "Person assigned to this stock take" },
  { id: "createdBy", name: "Created By", description: "Person who created this stock take" },
  { id: "date", name: "Date", description: "Created or scheduled date" },
];

// Default visible columns (all 12 columns)
const DEFAULT_VISIBLE_COLUMNS = [
  "id",
  "description",
  "tags",
  "type",
  "status",
  "warehouse",
  "recurrence",
  "progress",
  "discrepancies",
  "assignedTo",
  "createdBy",
  "date",
];

type SortColumn =
  | "id"
  | "description"
  | "type"
  | "status"
  | "warehouse"
  | "recurrence"
  | "progress"
  | "discrepancies"
  | "assignedTo"
  | "createdBy"
  | "date";
type SortDirection = "asc" | "desc";
type ViewFilter = "all" | "scheduled" | "one-time";

interface StockTakesPageProps {
  pageParams?: {
    view?: string;
  };
}

// Helper function to convert SavedStockTake to StockTake format
const convertSavedToStockTake = (saved: SavedStockTake): StockTake => {
  // Map old types to new types for backward compatibility
  let type: StockTakeType;
  if (saved.type === "Quick Count") {
    type = "Full Stock Take";
  } else if (["Full Stock Take", "Targeted Count", "Cycle Count"].includes(saved.type)) {
    type = saved.type as StockTakeType;
  } else {
    type = "Cycle Count";
  }

  return {
    id: saved.id,
    reference: saved.reference,
    description: saved.description,
    type,
    warehouse: saved.setupData?.warehouse || "",
    warehouseCode: saved.setupData?.warehouse || "",
    status: saved.status as StockTakeStatus,
    createdBy: saved.createdBy,
    createdAt: saved.createdAt,
    assignedTo: saved.setupData?.assignedTo || "Unassigned",
    items: [],
    totalItems: saved.countData?.countItems.length || 0,
    countedItems: saved.countData?.countItems.filter((item) => item.isCounted).length || 0,
    varianceCount: saved.countData?.countItems.filter((item) => item.variance !== 0).length || 0,
    notes: saved.reviewData?.notes,
    lastSavedAt: saved.lastModified,
    lastSavedBy: saved.createdBy,
    recurrence: "once",
  };
};

export function StockTakesPage({ pageParams }: StockTakesPageProps) {
  const { navigateTo } = useNavigation();
  const { isHelpOpen, helpContent, toggleHelp, setIsHelpOpen } = useContextualHelp("stock-takes");

  // Demo/Live mode toggle
  const { setMode, isDemo, isLive } = useScreenMode("stock-takes");
  const isLiveMode = isLive;

  // GraphQL hook for live mode
  const {
    stockTakes: backendStockTakes,
    pageInfo: stockTakesPageInfo,
    isLoading: isBackendLoading,
    fetchStockTakes,
  } = useStockTakes();

  // Load and merge saved stock takes with mock data (for demo mode)
  const loadMockStockTakes = () => {
    const savedStockTakes = getAllSavedStockTakes();
    const convertedSaved = savedStockTakes.map(convertSavedToStockTake);
    // Merge saved stock takes with mock data, with saved ones first
    return [...convertedSaved, ...mockStockTakes];
  };

  // State
  const [mockStockTakesData] = useState<StockTake[]>(loadMockStockTakes());

  // Cursor-based pagination state for live mode
  const [cursors, setCursors] = useState<string[]>([]);
  const [currentCursorIndex, setCurrentCursorIndex] = useState(0);

  // Handle mode toggle
  const handleModeToggle = useCallback(
    (newIsLive: boolean) => {
      setMode(newIsLive ? "live" : "demo");
      setCurrentPage(1);
      setCursors([]);
      setCurrentCursorIndex(0);
    },
    [setMode],
  );

  // Track previous filter values to detect changes and reset pagination
  const prevFiltersRef = useRef<{
    searchQuery: string;
    filterRows: string;
    sortColumn: string;
    sortDirection: string;
  } | null>(null);

  // Helper function to convert FilterRow[] to GraphQL filter input
  const mapFilterRowsToGraphQL = useCallback((filterRows: FilterRow[]): StockTakeFilterInput => {
    // Helper function to map a text filter row to GraphQL string filter format
    const mapTextFilterToGraphQL = (row: FilterRow): Record<string, string | boolean> | null => {
      switch (row.operator) {
        case "contains":
          return row.value ? { contains: String(row.value) } : null;
        case "doesNotContain":
          return row.value ? { doesNotContain: String(row.value) } : null;
        case "equals":
          return row.value ? { eq: String(row.value) } : null;
        case "notEquals":
          return row.value ? { ne: String(row.value) } : null;
        case "startsWith":
          return row.value ? { beginsWith: String(row.value) } : null;
        case "endsWith":
          return row.value ? { endsWith: String(row.value) } : null;
        case "isEmpty":
          return { isBlank: true };
        case "isNotEmpty":
          return { isNotBlank: true };
        default:
          return null;
      }
    };

    const filterInput: StockTakeFilterInput = {};

    for (const row of filterRows) {
      const textFilter = mapTextFilterToGraphQL(row);

      switch (row.field) {
        case "id":
        case "reference":
          if (textFilter) filterInput.stockTakeReference = textFilter;
          break;
        case "description":
          if (textFilter) filterInput.description = textFilter;
          break;
        case "type":
          if (textFilter) filterInput.stockTakeType = textFilter;
          break;
        case "status":
          if (textFilter) filterInput.status = textFilter;
          break;
        case "warehouse":
        case "warehouseCode":
          if (textFilter) filterInput.warehouse = textFilter;
          break;
      }
    }

    return filterInput;
  }, []);

  // Current data source based on mode
  const stockTakes = useMemo(() => {
    return isLiveMode ? backendStockTakes : mockStockTakesData;
  }, [isLiveMode, backendStockTakes, mockStockTakesData]);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [viewFilter, setViewFilter] = useState<ViewFilter>("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [warehouseFilter, setWarehouseFilter] = useState<string>("all");
  const [sortColumn, setSortColumn] = useState<SortColumn>("date");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  // Dialog states
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isCreateScheduleDialogOpen, setIsCreateScheduleDialogOpen] = useState(false);
  const [isDetailsDrawerOpen, setIsDetailsDrawerOpen] = useState(false);
  const [selectedStockTake, setSelectedStockTake] = useState<StockTake | null>(null);

  // Export state
  const [isExporting, setIsExporting] = useState(false);

  // Advanced Filters state
  const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);
  const [activeFilterRows, setActiveFilterRows] = useState<FilterRow[]>([]);
  const [activeFilterLogic, setActiveFilterLogic] = useState<FilterLogic[]>([]);
  const [activeFilterChips, setActiveFilterChips] = useState<ActiveFilterChip[]>([]);

  // Configure Columns state
  const [isConfigureColumnsModalOpen, setIsConfigureColumnsModalOpen] = useState(false);
  const [columnConfig, setColumnConfig] = useState<ColumnConfig>({
    visibleColumns: DEFAULT_VISIBLE_COLUMNS,
    columnOrder: DEFAULT_VISIBLE_COLUMNS,
  });

  // Dynamic filter fields based on existing columns (like warehouse-locations pattern)
  const dynamicFilterFields = useMemo((): FilterField[] => {
    return [
      { id: "id", label: "ID", type: "text" },
      { id: "reference", label: "Reference", type: "text" },
      { id: "description", label: "Description", type: "text" },
      {
        id: "type",
        label: "Type",
        type: "select",
        options: [
          { value: "Full Stock Take", label: "Full Stock Take" },
          { value: "Targeted Count", label: "Targeted Count" },
          { value: "Cycle Count", label: "Cycle Count" },
        ],
      },
      {
        id: "status",
        label: "Status",
        type: "select",
        options: [
          { value: "Draft", label: "Draft" },
          { value: "Scheduled", label: "Scheduled" },
          { value: "In Progress", label: "In Progress" },
          { value: "Paused", label: "Paused" },
          { value: "Completed", label: "Completed" },
          { value: "Cancelled", label: "Cancelled" },
        ],
      },
      { id: "warehouse", label: "Warehouse", type: "text" },
      { id: "warehouseCode", label: "Warehouse Code", type: "text" },
      { id: "assignedTo", label: "Assigned To", type: "text" },
      { id: "createdBy", label: "Created By", type: "text" },
      { id: "createdAt", label: "Created Date", type: "date" },
      { id: "totalItems", label: "Total Items", type: "number" },
      { id: "countedItems", label: "Counted Items", type: "number" },
      { id: "varianceCount", label: "Variance Count", type: "number" },
      { id: "progressPercent", label: "Progress %", type: "number" },
    ];
  }, []);

  // Load column configuration from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem("stockTakesColumnConfig");
      if (saved) {
        const parsed = JSON.parse(saved) as ColumnConfig;
        if (Array.isArray(parsed.visibleColumns) && Array.isArray(parsed.columnOrder)) {
          setColumnConfig(parsed);
        }
      }
    } catch {
      // ignore malformed data
    }
  }, []);

  // Handle URL parameter for view filter
  useEffect(() => {
    if (pageParams?.view === "scheduled") {
      setViewFilter("scheduled");
    }
  }, [pageParams]);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [viewFilter, typeFilter, statusFilter, warehouseFilter, debouncedSearchQuery, activeFilterRows]);

  // Fetch backend data when in live mode with cursor-based pagination
  useEffect(() => {
    if (isLiveMode) {
      // Check if filter-related values have changed (not pagination)
      const currentFilters = {
        searchQuery: debouncedSearchQuery,
        filterRows: JSON.stringify(activeFilterRows),
        sortColumn,
        sortDirection,
      };

      const filtersChanged =
        prevFiltersRef.current !== null &&
        (prevFiltersRef.current.searchQuery !== currentFilters.searchQuery ||
          prevFiltersRef.current.filterRows !== currentFilters.filterRows ||
          prevFiltersRef.current.sortColumn !== currentFilters.sortColumn ||
          prevFiltersRef.current.sortDirection !== currentFilters.sortDirection);

      // Update the ref with current values
      prevFiltersRef.current = currentFilters;

      // If filters changed, reset pagination and fetch from the beginning
      let effectiveCursorIndex = currentCursorIndex;
      if (filtersChanged) {
        setCursors([]);
        setCurrentCursorIndex(0);
        effectiveCursorIndex = 0;
      }

      // Build sort input based on current sort state - using backend OrderType format
      let sortInput: StockTakeSortInput | undefined = undefined;
      if (sortColumn === "id") {
        sortInput = { field: "StockTakeReference", order: SortOrder[sortDirection] };
      } else if (sortColumn === "description") {
        sortInput = { field: "Description", order: SortOrder[sortDirection] };
      } else if (sortColumn === "type") {
        sortInput = { field: "StockTakeType", order: SortOrder[sortDirection] };
      } else if (sortColumn === "status") {
        sortInput = { field: "Status", order: SortOrder[sortDirection] };
      } else if (sortColumn === "warehouse") {
        sortInput = { field: "Warehouse", order: SortOrder[sortDirection] };
      } else if (sortColumn === "date") {
        sortInput = { field: "CreatedAt", order: SortOrder[sortDirection] };
      }

      // Build filter input - start with search query filter
      const filterInput: StockTakeFilterInput = {};
      if (debouncedSearchQuery.trim()) {
        filterInput.stockTakeReference = { contains: debouncedSearchQuery };
      }

      // Merge advanced filters into the filter input
      const advancedFilterInput = mapFilterRowsToGraphQL(activeFilterRows);
      const mergedFilterInput: StockTakeFilterInput = { ...filterInput, ...advancedFilterInput };

      // Get the cursor for the current page
      const after = effectiveCursorIndex > 0 ? cursors[effectiveCursorIndex - 1] : undefined;

      fetchStockTakes({
        first: itemsPerPage,
        after,
        filter: Object.keys(mergedFilterInput).length > 0 ? mergedFilterInput : undefined,
        sort: sortInput,
      }).then(({ pageInfo }) => {
        // Store the end cursor for next page navigation
        if (pageInfo?.endCursor && effectiveCursorIndex === cursors.length) {
          setCursors((prev) => [...prev, pageInfo.endCursor!]);
        }
      });
    }
  }, [
    isLiveMode,
    debouncedSearchQuery,
    activeFilterRows,
    sortColumn,
    sortDirection,
    itemsPerPage,
    currentCursorIndex,
    cursors,
    fetchStockTakes,
    mapFilterRowsToGraphQL,
  ]);

  // Filter stock takes
  const filteredStockTakes = useMemo(() => {
    let filtered = stockTakes;

    // Apply advanced filters first
    if (activeFilterRows.length > 0) {
      filtered = applyFilters(filtered, activeFilterRows, activeFilterLogic);
    }

    // Apply view filter
    if (viewFilter === "scheduled") {
      filtered = filtered.filter((st) => st.recurrence && st.recurrence !== "once");
    } else if (viewFilter === "one-time") {
      filtered = filtered.filter((st) => !st.recurrence || st.recurrence === "once");
    }

    // Apply search
    if (debouncedSearchQuery.trim()) {
      const query = debouncedSearchQuery.toLowerCase();
      filtered = filtered.filter(
        (st) =>
          st.id.toLowerCase().includes(query) ||
          st.reference.toLowerCase().includes(query) ||
          st.description.toLowerCase().includes(query) ||
          (st.warehouse && st.warehouse.toLowerCase().includes(query)),
      );
    }

    // Apply type filter
    if (typeFilter !== "all") {
      filtered = filtered.filter((st) => st.type === typeFilter);
    }

    // Apply status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter((st) => st.status === statusFilter);
    }

    // Apply warehouse filter
    if (warehouseFilter !== "all") {
      if (warehouseFilter === "multi-warehouse") {
        filtered = filtered.filter((st) => !st.warehouse || st.warehouse === "");
      } else {
        filtered = filtered.filter((st) => st.warehouse === warehouseFilter);
      }
    }

    return filtered;
  }, [
    stockTakes,
    viewFilter,
    debouncedSearchQuery,
    typeFilter,
    statusFilter,
    warehouseFilter,
    activeFilterRows,
    activeFilterLogic,
  ]);

  // Sort stock takes
  const sortedStockTakes = useMemo(() => {
    const sorted = [...filteredStockTakes];

    sorted.sort((a, b) => {
      let aValue: string | number | Date;
      let bValue: string | number | Date;

      switch (sortColumn) {
        case "id":
          aValue = a.id;
          bValue = b.id;
          break;
        case "description":
          aValue = a.description;
          bValue = b.description;
          break;
        case "type":
          aValue = a.type;
          bValue = b.type;
          break;
        case "status":
          aValue = a.status;
          bValue = b.status;
          break;
        case "warehouse":
          aValue = a.warehouse || "";
          bValue = b.warehouse || "";
          break;
        case "recurrence":
          aValue = a.recurrence || "";
          bValue = b.recurrence || "";
          break;
        case "progress":
          aValue = a.totalItems > 0 ? a.countedItems / a.totalItems : 0;
          bValue = b.totalItems > 0 ? b.countedItems / b.totalItems : 0;
          break;
        case "discrepancies":
          aValue = a.varianceCount;
          bValue = b.varianceCount;
          break;
        case "assignedTo":
          aValue = a.assignedTo;
          bValue = b.assignedTo;
          break;
        case "createdBy":
          aValue = a.createdBy;
          bValue = b.createdBy;
          break;
        case "date":
          aValue = (a.nextScheduledDate || a.createdAt).getTime();
          bValue = (b.nextScheduledDate || b.createdAt).getTime();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [filteredStockTakes, sortColumn, sortDirection]);

  // Paginate sorted stock takes
  const paginatedStockTakes = useMemo(() => {
    // In live mode, data is already paginated from the backend
    if (isLiveMode) {
      return sortedStockTakes;
    }
    // In demo mode, paginate locally
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sortedStockTakes.slice(startIndex, endIndex);
  }, [sortedStockTakes, currentPage, itemsPerPage, isLiveMode]);

  // Calculate pagination values (different for live vs demo mode)
  const totalPages = isLiveMode
    ? stockTakesPageInfo?.hasNextPage
      ? currentCursorIndex + 2
      : currentCursorIndex + 1
    : Math.ceil(sortedStockTakes.length / itemsPerPage);
  const startItem = sortedStockTakes.length === 0 ? 0 : isLiveMode ? 1 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = isLiveMode ? sortedStockTakes.length : Math.min(currentPage * itemsPerPage, sortedStockTakes.length);

  // Handle sorting
  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  // Handle create stock take
  const handleCreateStockTake = () => {
    toast.success("Stock take created successfully");
    setIsCreateDialogOpen(false);
  };

  // Handle create schedule
  const handleCreateSchedule = () => {
    toast.success("Schedule created successfully");
    setIsCreateScheduleDialogOpen(false);
  };

  // Handle view details
  const handleViewDetails = (stockTake: StockTake) => {
    setSelectedStockTake(stockTake);
    setIsDetailsDrawerOpen(true);
  };

  // Handle resume stock take (for Draft and In Progress)
  const handleResumeStockTake = (stockTake: StockTake) => {
    // Check if this is a saved stock take (Draft or In Progress)
    if (stockTake.status === "Draft" || stockTake.status === "In Progress") {
      // Get the saved stock take data
      const savedStockTakes = getAllSavedStockTakes();
      const savedStockTake = savedStockTakes.find((st) => st.id === stockTake.id);

      if (savedStockTake) {
        // Navigate to the appropriate step based on currentStep
        if (savedStockTake.currentStep === 1) {
          navigateTo("stock-takes/quick-count/setup");
        } else if (savedStockTake.currentStep === 2) {
          navigateTo("stock-takes/quick-count/count", {
            formData: savedStockTake.setupData,
          });
        } else if (savedStockTake.currentStep === 3) {
          navigateTo("stock-takes/quick-count/review");
        }
        toast.info(`Resuming stock take: ${stockTake.description}`);
      } else {
        toast.error("Could not find saved stock take data");
      }
    } else {
      // For completed or other statuses, just view details
      handleViewDetails(stockTake);
    }
  };

  // Handle row click
  const handleRowClick = (stockTake: StockTake) => {
    // If it's a draft or in progress, allow resuming
    if (stockTake.status === "Draft" || stockTake.status === "In Progress") {
      handleResumeStockTake(stockTake);
    } else {
      // Otherwise, just view details
      handleViewDetails(stockTake);
    }
  };

  // Handle pause schedule
  const handlePauseSchedule = (stockTake: StockTake) => {
    if (stockTake.isSystemGenerated) {
      toast.info("System-generated schedules are managed in Stock Options");
    } else {
      toast.success(`Schedule "${stockTake.name}" paused`);
    }
  };

  // Handle resume schedule
  const handleResumeSchedule = (stockTake: StockTake) => {
    toast.success(`Schedule "${stockTake.name}" resumed`);
  };

  // Handle run now
  const handleRunNow = (stockTake: StockTake) => {
    toast.success(`Creating stock take from schedule "${stockTake.name}"`);
  };

  // Handle advanced filters
  const handleApplyFilters = (rows: FilterRow[], logic: FilterLogic[]) => {
    setActiveFilterRows(rows);
    setActiveFilterLogic(logic);
    setActiveFilterChips(generateFilterChips(rows, logic));
    toast.success(`${rows.length} filter${rows.length !== 1 ? "s" : ""} applied`);
  };

  const handleRemoveFilterChip = (chipId: string) => {
    const newRows = activeFilterRows.filter((row) => row.id !== chipId);
    const index = activeFilterRows.findIndex((row) => row.id === chipId);
    const newLogic = [...activeFilterLogic];

    if (index > 0) {
      newLogic.splice(index - 1, 1);
    } else if (newLogic.length > 0) {
      newLogic.splice(0, 1);
    }

    setActiveFilterRows(newRows);
    setActiveFilterLogic(newLogic);
    setActiveFilterChips(generateFilterChips(newRows, newLogic));

    if (newRows.length === 0) {
      toast.success("All filters cleared");
    }
  };

  const handleClearAllFilters = () => {
    setActiveFilterRows([]);
    setActiveFilterLogic([]);
    setActiveFilterChips([]);
    toast.success("All filters cleared");
  };

  // Handle configure columns
  const handleSaveColumnConfig = (config: ColumnConfig) => {
    setColumnConfig(config);
    localStorage.setItem("stockTakesColumnConfig", JSON.stringify(config));
  };

  // Get visible columns helper
  const getVisibleColumns = () => {
    return columnConfig.columnOrder.filter((colId) => columnConfig.visibleColumns.includes(colId));
  };

  // Check if column is visible
  const isColumnVisible = (columnId: string) => {
    return columnConfig.visibleColumns.includes(columnId);
  };

  // Handle items per page change
  const handleItemsPerPageChange = (value: string) => {
    setItemsPerPage(Number(value));
    setCurrentPage(1); // Reset to first page
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      // Show all pages if total is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (currentPage > 3) {
        pages.push("...");
      }

      // Show pages around current page
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (currentPage < totalPages - 2) {
        pages.push("...");
      }

      // Always show last page
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  // Handle export
  const handleExport = async (stockTake: StockTake, format: "csv" | "excel") => {
    setIsExporting(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 500));

      const headers = ["Name", "Type", "Status", "Warehouse", "Items", "Date"];
      const data = [
        {
          Name: stockTake.name,
          Type: stockTake.type,
          Status: stockTake.status,
          Warehouse: stockTake.warehouse || "All Warehouses",
          Items: stockTake.itemsToCount.toString(),
          Date: (stockTake.nextScheduledDate || stockTake.createdAt).toLocaleDateString(),
        },
      ];

      const filename = `stock-take-${stockTake.name.replace(/\s+/g, "-").toLowerCase()}-${getCurrentDateForFilename()}`;

      if (format === "csv") {
        exportToCSV(data, headers, `${filename}.csv`);
        toast.success("Exported to CSV");
      } else {
        exportToExcel(data, headers, `${filename}.xlsx`);
        toast.success("Exported to Excel");
      }
    } catch (error) {
      toast.error("Export failed. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    const total = stockTakes.length;
    const draft = stockTakes.filter((st) => st.status === "Draft").length;
    const scheduled = stockTakes.filter((st) => st.status === "Scheduled").length;
    const active = stockTakes.filter((st) => st.status === "Active").length;
    const inProgress = stockTakes.filter((st) => st.status === "In Progress").length;
    const completed = stockTakes.filter((st) => st.status === "Completed" || st.status === "Complete").length;

    return { total, draft, scheduled, active, inProgress, completed };
  }, [stockTakes]);

  // Render table cell based on column ID
  const renderTableCell = (stockTake: StockTake, columnId: string) => {
    const isScheduled = stockTake.recurrence && stockTake.recurrence !== "once";

    switch (columnId) {
      case "id":
        return <TableCell className="font-mono text-sm">{stockTake.id}</TableCell>;

      case "description":
        return <TableCell className="font-medium">{stockTake.description}</TableCell>;

      case "tags":
        return (
          <TableCell>
            <div className="flex items-center gap-2">
              {stockTake.isSystemGenerated && (
                <Badge className="bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-400">
                  <Zap className="h-3 w-3 mr-1" />
                  Auto
                </Badge>
              )}
              {stockTake.abcClass && (
                <Badge
                  variant="outline"
                  className="bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400"
                >
                  <BarChart3 className="h-3 w-3 mr-1" />
                  {stockTake.abcClass}-Class
                </Badge>
              )}
              {!stockTake.isSystemGenerated && !stockTake.abcClass && (
                <span className="text-muted-foreground text-sm">-</span>
              )}
            </div>
          </TableCell>
        );

      case "type":
        return (
          <TableCell>
            <Badge className={getTypeColor(stockTake.type)}>{stockTake.type}</Badge>
          </TableCell>
        );

      case "status":
        return (
          <TableCell>
            <Badge className={getStatusColor(stockTake.status)}>{stockTake.status}</Badge>
          </TableCell>
        );

      case "warehouse":
        return (
          <TableCell>{stockTake.warehouse || <span className="text-muted-foreground">All Warehouses</span>}</TableCell>
        );

      case "recurrence":
        return (
          <TableCell>
            {isScheduled ? (
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-blue-600" />
                <span>{formatRecurrence(stockTake.recurrence!)}</span>
              </div>
            ) : (
              <span className="text-muted-foreground">One-time</span>
            )}
          </TableCell>
        );

      case "progress":
        return (
          <TableCell>
            <div className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <span>
                  {stockTake.countedItems} / {stockTake.totalItems}
                </span>
                <span className="text-muted-foreground">{calculateProgress(stockTake)}%</span>
              </div>
              <Progress value={calculateProgress(stockTake)} className="h-2" />
            </div>
          </TableCell>
        );

      case "discrepancies":
        return (
          <TableCell>
            {stockTake.varianceCount > 0 ? (
              <div className="flex items-center gap-2">
                <span className="font-medium text-orange-600">{stockTake.varianceCount}</span>
              </div>
            ) : (
              <span className="text-muted-foreground">-</span>
            )}
          </TableCell>
        );

      case "assignedTo":
        return <TableCell>{stockTake.assignedTo}</TableCell>;

      case "createdBy":
        return <TableCell>{stockTake.createdBy}</TableCell>;

      case "date":
        return <TableCell>{(stockTake.nextScheduledDate || stockTake.createdAt).toLocaleDateString()}</TableCell>;

      default:
        return <TableCell>-</TableCell>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold tracking-tight">Stock Takes & Schedules</h1>
          </div>
          <p className="text-muted-foreground mt-2">
            Manage stock takes and recurring schedules for inventory counting.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <GlobalModeToggle isLive={isLiveMode} onToggle={handleModeToggle} isLoading={isBackendLoading} />
          <HelpButton onClick={toggleHelp} />
          <Button onClick={() => navigateTo("stock-takes-home")}>
            <Plus className="h-4 w-4 mr-2" />
            New Stock Take
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <StaticMetricCardAlt
          title="Total"
          value={summaryStats.total.toString()}
          icon={ClipboardList}
          variant="info"
          changeLabel="All stock takes"
        />
        <StaticMetricCardAlt
          title="Scheduled"
          value={summaryStats.scheduled.toString()}
          icon={Clock}
          variant="info"
          changeLabel="Recurring stock takes"
        />
        <StaticMetricCardAlt
          title="Draft"
          value={summaryStats.draft.toString()}
          icon={Edit}
          variant="warning"
          changeLabel="Not yet started"
        />
        <StaticMetricCardAlt
          title="In Progress"
          value={summaryStats.inProgress.toString()}
          icon={PlayCircle}
          variant="warning"
          changeLabel="Currently counting"
        />
        <StaticMetricCardAlt
          title="Completed"
          value={summaryStats.completed.toString()}
          icon={CheckCircle2}
          variant="success"
          changeLabel="Finished stock takes"
        />
      </div>

      {/* View Filter Tabs */}
      <Tabs value={viewFilter} onValueChange={(value) => setViewFilter(value as ViewFilter)}>
        <TabsList>
          <TabsTrigger value="all">All Stock Takes ({filteredStockTakes.length})</TabsTrigger>
          <TabsTrigger value="scheduled">
            Scheduled ({filteredStockTakes.filter((st) => st.recurrence && st.recurrence !== "once").length})
          </TabsTrigger>
          <TabsTrigger value="one-time">
            One-time ({filteredStockTakes.filter((st) => !st.recurrence || st.recurrence === "once").length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value={viewFilter} className="mt-4">
          {/* Filters and Search */}
          <div className="flex items-center gap-4 flex-wrap">
            <div className="relative w-[300px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search stock takes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-9"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="Full Stock Take">Full Stock Take</SelectItem>
                <SelectItem value="Targeted Count">Targeted Count</SelectItem>
                <SelectItem value="Cycle Count">Cycle Count</SelectItem>
              </SelectContent>
            </Select>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="Scheduled">Scheduled</SelectItem>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Paused">Paused</SelectItem>
                <SelectItem value="In Progress">In Progress</SelectItem>
                <SelectItem value="Completed">Completed</SelectItem>
                <SelectItem value="Cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>

            <Select value={warehouseFilter} onValueChange={setWarehouseFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Filter by warehouse" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Warehouses</SelectItem>
                <SelectItem value="multi-warehouse">Multi-warehouse</SelectItem>
                {mockWarehouses.map((warehouse) => (
                  <SelectItem key={warehouse.code} value={warehouse.name}>
                    {warehouse.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="ml-auto flex items-center gap-2">
              <Button variant="outline" onClick={() => setIsFiltersModalOpen(true)}>
                <Filter className="h-4 w-4 mr-2" />
                Advanced Filters
                {activeFilterChips.length > 0 && (
                  <Badge variant="secondary" className="ml-2">
                    {activeFilterChips.length}
                  </Badge>
                )}
              </Button>

              <Button variant="outline" onClick={() => setIsConfigureColumnsModalOpen(true)}>
                <Settings2 className="h-4 w-4 mr-2" />
                Configure Columns
              </Button>
            </div>

            {(searchQuery ||
              viewFilter !== "all" ||
              typeFilter !== "all" ||
              statusFilter !== "all" ||
              warehouseFilter !== "all") && (
              <Button
                variant="ghost"
                onClick={() => {
                  setSearchQuery("");
                  setViewFilter("all");
                  setTypeFilter("all");
                  setStatusFilter("all");
                  setWarehouseFilter("all");
                  setCurrentPage(1);
                }}
              >
                Clear Filters
              </Button>
            )}
          </div>

          {/* Active Filter Chips */}
          {activeFilterChips.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-muted-foreground">Active filters:</span>
              {activeFilterChips.map((chip) => (
                <Badge key={chip.id} variant="secondary" className="gap-1 pr-1">
                  {chip.displayText}
                  <button
                    onClick={() => handleRemoveFilterChip(chip.id)}
                    className="ml-1 hover:bg-muted rounded-sm p-0.5"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
              <Button variant="ghost" size="sm" onClick={handleClearAllFilters} className="h-7">
                Clear all
              </Button>
            </div>
          )}

          {/* Stock Takes Table */}
          {sortedStockTakes.length > 0 ? (
            <div className="rounded-lg border bg-card">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {getVisibleColumns().map((columnId) => {
                        const column = ALL_COLUMNS.find((col) => col.id === columnId);
                        const isSortable = [
                          "id",
                          "description",
                          "type",
                          "status",
                          "warehouse",
                          "recurrence",
                          "progress",
                          "discrepancies",
                          "assignedTo",
                          "createdBy",
                          "date",
                        ].includes(columnId);

                        return (
                          <TableHead
                            key={columnId}
                            className={`font-semibold ${isSortable ? "cursor-pointer hover:bg-muted/50" : ""}`}
                            onClick={isSortable ? () => handleSort(columnId as SortColumn) : undefined}
                          >
                            {column?.name || columnId}
                          </TableHead>
                        );
                      })}
                      <TableHead className="font-semibold text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedStockTakes.map((stockTake, index) => {
                      const isScheduled = stockTake.recurrence && stockTake.recurrence !== "once";
                      return (
                        <TableRow
                          key={stockTake.id}
                          className={`${index % 2 === 0 ? "bg-muted/30" : ""} cursor-pointer hover:bg-muted/50`}
                          onClick={() => handleRowClick(stockTake)}
                        >
                          {/* Render visible columns dynamically */}
                          {getVisibleColumns().map((columnId) => (
                            <React.Fragment key={columnId}>{renderTableCell(stockTake, columnId)}</React.Fragment>
                          ))}
                          <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  Actions
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {stockTake.isSystemGenerated && (
                                  <>
                                    <DropdownMenuLabel className="flex items-center gap-2">
                                      <Zap className="h-4 w-4" />
                                      System Schedule
                                    </DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                  </>
                                )}
                                <DropdownMenuItem onClick={() => handleViewDetails(stockTake)}>
                                  <Eye className="h-4 w-4 mr-2" />
                                  View Details
                                </DropdownMenuItem>
                                {isScheduled && (
                                  <>
                                    {stockTake.status === "Active" ? (
                                      <DropdownMenuItem onClick={() => handlePauseSchedule(stockTake)}>
                                        <Pause className="h-4 w-4 mr-2" />
                                        Pause Schedule
                                      </DropdownMenuItem>
                                    ) : stockTake.status === "Paused" ? (
                                      <DropdownMenuItem onClick={() => handleResumeSchedule(stockTake)}>
                                        <Play className="h-4 w-4 mr-2" />
                                        Resume Schedule
                                      </DropdownMenuItem>
                                    ) : null}
                                    <DropdownMenuItem onClick={() => handleRunNow(stockTake)}>
                                      <PlayCircle className="h-4 w-4 mr-2" />
                                      Run Now
                                    </DropdownMenuItem>
                                  </>
                                )}
                                {!isScheduled &&
                                  (stockTake.status === "Draft" || stockTake.status === "In Progress") && (
                                    <DropdownMenuItem onClick={() => handleResumeStockTake(stockTake)}>
                                      <Edit className="h-4 w-4 mr-2" />
                                      Resume Stock Take
                                    </DropdownMenuItem>
                                  )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => handleExport(stockTake, "csv")} disabled={isExporting}>
                                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                                  Export CSV
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => handleExport(stockTake, "excel")}
                                  disabled={isExporting}
                                >
                                  <Download className="h-4 w-4 mr-2" />
                                  Export Excel
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination Controls */}
              <div className="flex items-center justify-between p-4 border-t">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Items per page:</span>
                  <Select value={itemsPerPage.toString()} onValueChange={handleItemsPerPageChange}>
                    <SelectTrigger className="w-[80px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="25">25</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                  <span className="text-sm text-muted-foreground ml-4">
                    Showing {startItem}-{endItem} of {sortedStockTakes.length} items
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  {isLiveMode ? (
                    <>
                      {/* First Page Button - Live Mode */}
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => {
                          setCursors([]);
                          setCurrentCursorIndex(0);
                        }}
                        disabled={currentCursorIndex === 0 || isBackendLoading}
                      >
                        <ChevronsLeft className="h-4 w-4" />
                      </Button>

                      {/* Previous Page Button - Live Mode */}
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => {
                          if (currentCursorIndex > 0) {
                            setCurrentCursorIndex(currentCursorIndex - 1);
                          }
                        }}
                        disabled={currentCursorIndex === 0 || isBackendLoading}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>

                      <span className="text-sm text-muted-foreground px-2">Page {currentCursorIndex + 1}</span>

                      {/* Next Page Button - Live Mode */}
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => {
                          if (stockTakesPageInfo?.hasNextPage) {
                            setCurrentCursorIndex(currentCursorIndex + 1);
                          }
                        }}
                        disabled={!stockTakesPageInfo?.hasNextPage || isBackendLoading}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </>
                  ) : (
                    <>
                      {/* First Page Button - Demo Mode */}
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                      >
                        <ChevronsLeft className="h-4 w-4" />
                      </Button>

                      {/* Previous Page Button - Demo Mode */}
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setCurrentPage(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>

                      {/* Page Number Buttons - Demo Mode */}
                      {getPageNumbers().map((page, index) => (
                        <Button
                          key={index}
                          variant={page === currentPage ? "default" : "outline"}
                          size="icon"
                          onClick={() => typeof page === "number" && setCurrentPage(page)}
                          disabled={typeof page === "string"}
                          className={typeof page === "string" ? "cursor-default" : ""}
                        >
                          {page}
                        </Button>
                      ))}

                      {/* Next Page Button - Demo Mode */}
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>

                      {/* Last Page Button - Demo Mode */}
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages}
                      >
                        <ChevronsRight className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <Card className="p-12 text-center">
              <div className="flex flex-col items-center gap-4">
                <div className="rounded-full bg-muted p-3">
                  <ClipboardList className="h-6 w-6 text-muted-foreground" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-1">No stock takes found</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    {searchQuery ||
                    viewFilter !== "all" ||
                    typeFilter !== "all" ||
                    statusFilter !== "all" ||
                    warehouseFilter !== "all"
                      ? "Try adjusting your search or filters"
                      : "Get started by creating your first stock take"}
                  </p>
                  <Button onClick={() => setIsCreateDialogOpen(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Stock Take
                  </Button>
                </div>
              </div>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <CreateStockTakeDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        onSave={handleCreateStockTake}
      />

      <CreateScheduleDialog
        open={isCreateScheduleDialogOpen}
        onOpenChange={setIsCreateScheduleDialogOpen}
        onSuccess={handleCreateSchedule}
      />

      <StockTakeDetailsDrawer
        open={isDetailsDrawerOpen}
        onOpenChange={setIsDetailsDrawerOpen}
        stockTake={selectedStockTake}
        onSave={() => {}}
        onExport={handleExport}
      />

      {/* Advanced Filters Modal */}
      <AdvancedFiltersModal
        open={isFiltersModalOpen}
        onOpenChange={setIsFiltersModalOpen}
        onApplyFilters={handleApplyFilters}
        initialFilters={activeFilterRows}
        initialLogic={activeFilterLogic}
        entityType="stock-takes"
        customFields={dynamicFilterFields}
      />

      {/* Configure Columns Modal */}
      <ConfigureColumnsModal
        open={isConfigureColumnsModalOpen}
        onOpenChange={setIsConfigureColumnsModalOpen}
        columns={ALL_COLUMNS}
        currentConfig={columnConfig}
        onSave={handleSaveColumnConfig}
        defaultConfig={{ visibleColumns: DEFAULT_VISIBLE_COLUMNS, columnOrder: DEFAULT_VISIBLE_COLUMNS }}
      />

      {/* Help Drawer */}
      <ContextualHelpDrawer
        open={isHelpOpen}
        onOpenChange={setIsHelpOpen}
        helpContent={helpContent}
        onNavigateToPage={(pageId) => navigateTo(pageId as any)}
      />
    </div>
  );
}
